import { CommonModule } from '@angular/common';
import { inject, Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { MapThemeDirective } from 'src/app/directives/map-theme.directive';
import * as ThreeDTool from "src/app/classes/shared/threeDTools";
import { createHeatmapOverlay } from 'src/app/classes/maps/heatmap-overlay';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';

export interface MapPoint {
  lat: number;
  lng: number;
  weight: any;                      // a number (opacity) or a tag (colour)
}

const METERS_PER_DEGREE = 111320;

// Points closer than the cell (in meters) drawn as one circle, the strongest weight kept per tag:
// the same picture with far fewer Google Maps overlays (each circle is one, and a widget can hold thousands of points).
export function mergeMapPoints(points: MapPoint[], cellMeters: number): MapPoint[] {
  if (Number.isNaN(cellMeters) || cellMeters <= 0) return points;

  const cell = cellMeters / METERS_PER_DEGREE;
  const merged = new Map<string, MapPoint>();
  for (const point of points) {
    const tag = typeof point.weight === 'number' ? '' : String(point.weight);
    const key = `${Math.round(point.lat / cell)}:${Math.round(point.lng / cell)}:${tag}`;
    const current = merged.get(key);
    if (!current) merged.set(key, { ...point });
    else if (typeof point.weight === 'number' && point.weight > current.weight) current.weight = point.weight;
  }
  return [...merged.values()];
}

@Component({
  selector: 'app-maps',
  imports: [
    WidgetLoadingComponent,
    WidgetTitleComponent,
    CommonModule,
    GoogleMapsModule,
    MapThemeDirective
  ],
  standalone: true,
  templateUrl: './maps.component.html',
  styleUrl: './maps.component.scss'
})

export class MapsComponent extends WidgetDirective {
  // Component API spécifique à MapsComponent
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild('mapContainer') mapContainer!: ElementRef;
  @ViewChild('legendMap', { static: false }) legendMap!: ElementRef;

  // Variables spécifiques à MapsComponent
  containerStyle: string = "";
  blockStyle: string = "width:100%;display:flex;flex-direction: column;";
  mapStyle: string = "width:100px;height:100px;";
  titleStyle: string = "font-size:16px;margin:0px 20px 20px;";
  titlePosition: string = "top";
  ready = false;
  heatmap?: google.maps.OverlayView;
  mapMarkers: any[] = [];
  polygonZones: any = {};
  groundOverlay: any;
  labelsWithColors: { label: string; color: any; }[] = [];
  showLegend: boolean = false;
  legendStyle: string = "";
  style: string = "";
  mapOptions: google.maps.MapOptions = {
    center: { lat: -34.397, lng: 150.644 },
    zoom: 8,
  };

  private isMapInitialized: boolean = false;
  private isVisibleInViewport: boolean = false;
  private intersectionObserver: IntersectionObserver | null = null;
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly zone: NgZone = inject(NgZone);

  override ngOnInit(): void {
    this.resetStyles();
    super.ngOnInit();
  }

  override ngAfterViewInit(): void {
    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        this.isVisibleInViewport = entry.isIntersecting;

        if (this.isVisibleInViewport && !this.isMapInitialized && this.widget) {
          this.isMapInitialized = true;
          this.init();
          this.intersectionObserver?.disconnect();
        }
      },
      { threshold: 0.01 }
    );

    // The map container only exists once loaded: the widget element itself tells the visibility.
    this.intersectionObserver.observe(this.host.nativeElement);
  }

  override ngOnChanges(): void {
    if (!this.isMapInitialized && !this.isVisibleInViewport) {
      return;
    }

    super.ngOnChanges?.();
  }

  override ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.heatmap?.setMap(null);

    if (this.mapMarkers.length) {
      this.mapMarkers.forEach(marker => marker.setMap(null));
      this.mapMarkers.length = 0;
    }

    Object.values(this.polygonZones).forEach((value: any) => {
      value.polygon?.setMap(null);
    });

    this.removeOverlay();
    super.ngOnDestroy();
  }

  // Implémentation de la méthode abstraite
  init(): void {
    if (!this.widget?.visible || this.resize) return;

    this.widgetDesign = this.widget.design;
    this.widgetData = this.widget.data;
    this.widgetQuery = this.widget.query;

    if (this.type !== this.widget.type) {
      this.resetStyles();
      this.type = this.widget.type;
    }

    this.initOptions();
    this.withContent(() => this.updateMap());
  }

  resetStyles(): void {
    this.blockStyle = "width:100%;display:flex;flex-direction: column;align-content:center;";
    this.mapStyle = "width:100%;";
    this.titleStyle = "font-size:16px;margin:0px 20px 20px;";
    this.containerStyle = "";
  }

  initOptions(): void {
    try {
      this.resetStyles();
      const options = this.widgetDesign;

      if (!options) return;

      this.containerStyle = `background-color: var(--background-color);`;
      if (options.title) {
        this.showTitle = options.title.display == undefined ? true : options.title.display;
        this.titlePosition = options.title.position || 'top';
        this.titleStyle = `font-size:${options.title.fontSize || '16'}px; margin:10px 20px 20px;`;
        this.blockStyle += `height:100%;color: var(--text-color); text-align:${options.title.align || 'center'};`;

        if (options.legendMap) {
          this.legendStyle = `font-size:${options.legendMap.fontSize || '12'}px; align-items:${options.legendMap.align || 'start'};`;
        }
      }

      this.cdr.detectChanges();
    } catch (e: any) {
      console.error("Error initializing widget options:", e);
    }
  }

  updateMap(): void {
    if (!this.mapContainer || !this.widgetDesign) return;

    const mapData: { location: google.maps.LatLng; weight: any; }[] = [];
    let lats = 0, lons = 0;
    const max = { lat: -90, lon: -180 };
    const min = { lat: 90, lon: 180 };
    let maxWeight = 0;

    // Get bounds
    if (this.widgetData) {
      this.widgetData.forEach((d: any) => {
        if (d.x && d.y && d.x > -180 && d.x < 180) {
          if (Array.isArray(d.y)) d.y = d.y[0];
          if (isNaN(d.y) || d.y < -90 || d.y > 90) return;

          lats += d.y;
          lons += d.x;
          max.lat = Math.max(max.lat, d.y);
          min.lat = Math.min(min.lat, d.y);
          max.lon = Math.max(max.lon, d.x);
          min.lon = Math.min(min.lon, d.x);

          if (typeof d.s == "number") {
            maxWeight = Math.max(maxWeight, d.s);
          }

          mapData.push({
            location: new google.maps.LatLng(d.y, d.x),
            weight: d.s !== undefined ? d.s : 1
          });
        }
      });
    }

    let center: google.maps.LatLng;
    let zoom: number = 19;

    if (mapData.length) {
      center = new google.maps.LatLng(lats / mapData.length, lons / mapData.length);
    } else {
      center = new google.maps.LatLng(48.85837, 2.294432);
      zoom = 10;
    }

    let mapTypeId: google.maps.MapTypeId = google.maps.MapTypeId.ROADMAP;
    let opacity: number = 0.5;
    let radius: number = 10;
    const options = this.widgetDesign.map;

    this.removeOverlay();

    Object.keys(this.polygonZones).forEach(k => {
      this.polygonZones[k].polygon.setMap(null);
      this.polygonZones[k].polygon.setVisible(false);
    });

    if (options.showOverlay && this.widget.content && this.widget.content.model) {
      this.setOverlay(this.widget.content.model);
    } else {
      this.removeOverlay();
    }

    if (options.showZones && this.widget.content && this.widget.content.zones) {
      this.widget.content.zones.forEach((z: any) => {
        this.setZone(z);
      });

      Object.keys(this.polygonZones).forEach(k => {
        const z = this.polygonZones[k].zone;

        if (!options.zoneList.includes(z.name) || !options.showZones) {
          z.visible = false;
          this.polygonZones[k].polygon.setMap(null);
          this.polygonZones[k].polygon.setVisible(false);
        } else {
          z.visible = true;
        }
      });
    }

    this.width = this.mapContainer.nativeElement.offsetWidth + 2;
    this.height = this.mapContainer.nativeElement.offsetHeight;

    if (options) {
      if (options.useCustomCoordinate) {
        center = new google.maps.LatLng(options.customLat, options.customLon);
        zoom = options.customZoom;
      }

      if (options.type) {
        mapTypeId = options.type;
      }

      if (options.pointOpacity) {
        opacity = parseFloat(options.pointOpacity);
      }

      if (options.pointRadius) {
        radius = parseFloat(options.pointRadius);
      }
    }

    if (this.map && this.map.googleMap) {
      // Rendered on tiles loaded (reports wait for it): set before the map moves, run in Angular (map runs outside).
      google.maps.event.addListenerOnce(this.map.googleMap, 'tilesloaded', () => this.zone.run(() => this.widgetRendered = true));
      this.map.googleMap.setCenter(center);
      this.map.googleMap.setMapTypeId(mapTypeId);
      this.map.googleMap.setZoom(zoom);
    }

    if (options && !options.useCustomCoordinate) {
      this.map.fitBounds(
        new google.maps.LatLngBounds(
          new google.maps.LatLng(max.lat + 0.0002, min.lon - 0.0002),
          new google.maps.LatLng(min.lat - 0.0002, max.lon + 0.0002)
        )
      );
    }

    if (this.heatmap) {
      this.heatmap.setMap(null);
    }

    if (this.mapMarkers.length) {
      this.mapMarkers.forEach(m => m.setMap(null));
      this.mapMarkers = [];
    }

    if (this.widget.graphType == "geoHeatmap") {
      this.heatmap = createHeatmapOverlay({ data: mapData, radius, opacity });

      this.heatmap.setMap(this.map.googleMap ?? null);
    } else {
      const points = mergeMapPoints(mapData.map(d => ({ lat: d.location.lat(), lng: d.location.lng(), weight: d.weight })), radius / 2);
      points.forEach(d => {
        let color = this.widgetDesign.colors.colorList[0];

        if (typeof d.weight == "number") {
          opacity = d.weight * options.pointOpacity / maxWeight;
        } else {
          color = this.widgetDesign.colors.colorList[this.galleryTool.getTagName(d.weight)];
          opacity = options.pointOpacity;
        }

        const marker = new google.maps.Circle({
          fillColor: color,
          fillOpacity: opacity,
          strokeOpacity: 0,
          center: { lat: d.lat, lng: d.lng },
          map: this.map.googleMap,
          radius: options.pointRadius
        });

        this.mapMarkers.push(marker);
      });
    }

    this.showLegend = this.widgetDesign.legendMap?.showLegend;

    if (this.widget.data && this.widget.data.length > 0 && this.showLegend) {
      this.parseMetrics();
      const uniqueLabels = new Set<string>();

      this.widget.data.forEach((d: { s: string; }) => {
        if (d.s !== undefined && d.s !== null && d.s !== '') {
          uniqueLabels.add(d.s);
        }
      });

      const uniqueLabelsArray = Array.from(uniqueLabels);

      this.labelsWithColors = uniqueLabelsArray.map(label => {
        return {
          label: this.galleryTool.getDisplayName(label),
          color: this.widgetDesign.colors.colorList[this.galleryTool.getTagName(label)] || '#000000'
        };
      });

      if (this.showLegend && this.legendMap) {
        const legend = this.legendMap.nativeElement;

        if (this.map && this.map.controls && this.map.controls[google.maps.ControlPosition.BOTTOM_LEFT]) {
          this.map.controls[google.maps.ControlPosition.BOTTOM_LEFT].push(legend);
        }
      }
    }

  }

  setOverlay(overlay: { mapImage: string; mapCoord: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | null; }): void {
    this.groundOverlay = new google.maps.GroundOverlay(overlay.mapImage, overlay.mapCoord);
    this.groundOverlay.setMap(this.map.googleMap);
  }

  removeOverlay(): void {
    if (this.groundOverlay) {
      this.groundOverlay.setMap(null);
    }
  }

  setZone(zone: { polygon: any[]; color: any; id: string | number; }): void {
    try {
      const positions: { lat: any; lng: any; }[] = [];
      let latLon = [];

      zone.polygon.forEach(t => {
        latLon = ThreeDTool.getLatLonFromXYZ2(t[0], t[1], 0);
        positions.push({ lat: latLon[1], lng: latLon[0] });
      });

      const zoneColor = this.convertColors(zone.color);

      const polygon = new google.maps.Polygon({
        paths: positions,
        strokeColor: 'red',
        fillColor: zoneColor,
        strokeWeight: 2,
        fillOpacity: 0.45
      });

      polygon.setMap(this.map.googleMap ?? null);

      this.polygonZones[zone.id] = {
        zone,
        polygon
      };
    } catch (e) {
      console.error(e);
    }
  }

  convertColors(color: string): string {
    const array = [
      { title: 'primary', value: '#87CEEB' },
      { title: 'danger', value: '#bb2124' },
      { title: 'success', value: "#22bb33" },
      { title: 'warning', value: "#f0ad4e" },
      { title: 'info', value: "#5bc0de" }
    ];

    array.forEach(t => {
      if (color === t.title) color = t.value;
    });

    return color;
  }

  setLoading(state: boolean): void {
    if (state) {
      this.loading.push(1);
    } else {
      this.loading.pop();
    }
  }

  override validationGraphOption(): boolean {
    if (this.widget.graphOption && Array.isArray(this.widget.graphOption)) {
      const missingRequiredOptions = this.widget.graphOption.filter((option: any) => {
        if (!option.required) return false;

        const query = this.widget.query;

        switch (option.input) {
          case 'longitude':
            return !query.longitude || Object.keys(query.longitude).length === 0;
          case 'latitude':
            return !query.latitude || Object.keys(query.latitude).length === 0;
          default:
            return false;
        }
      });

      if (missingRequiredOptions.length > 0) {
        this.error = `Configuration incomplète : ${missingRequiredOptions.map((opt: any) => opt.name).join(', ')}`;
        this.widgetData = null;
        this.widget.data = [];
        this.dataByProject = {};
        this.removeOverlay();
        return false;
      }
    }

    return true;
  }

}